import { useState, useEffect, useRef, useContext, useCallback } from "react";
import app from "nystem";
import { Wrapper, Button, Loading, DatabaseSearchContext, ContentTypeRender, useLocation } from "nystem-components";

const getScrollParent = (element) => {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    if (parent === document.body || parent === document.documentElement) return window;
    if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) return parent;
  }
  return window;
};

export const LoadingItems = ({ model }) => (
  <Wrapper className={model.loadingClassName}>
    {Array.from({ length: model.loadingCount || 1 }, (und, index) => (
      <ContentTypeRender key={index} path={model.path} items={model.loadingItem} />
    ))}
  </Wrapper>
);

const InfiniteSlot = ({ slot, top, height, search, model, onMeasure, onRetry }) => {
  const ref = useRef();
  const [ready, setReady] = useState(false);
  const loaded = !!search?.data;
  useEffect(() => {
    if (!loaded) {
      setReady(false);
      return;
    }
    const element = ref.current;
    let timer;
    // Rows render nothing during Loading's 200ms delay and a loading bar after it, and lazy components
    // render a suspense placeholder. Only a height that outlasts that delay without these is the batch's own.
    // A placeholder can resolve without changing the batch height, so the check repeats until they are gone.
    // An empty or hidden batch is measured again by the observer when it gets a height.
    const measure = () => {
      if (!element.offsetHeight) return;
      if (element.querySelector(".loading, .render-suspense-loading")) {
        timer = setTimeout(measure, 250);
        return;
      }
      setReady(true);
      onMeasure(slot, element.offsetHeight);
    };
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(measure, 250);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [loaded, onMeasure, slot]);

  return (
    <div
      data-infinite-slot={slot}
      style={{ position: "absolute", top, width: "100%", minHeight: ready ? undefined : height }}
    >
      {loaded && (
        <div
          ref={ref}
          data-infinite-content
          className="transition-opacity duration-300"
          style={{ opacity: ready ? 1 : 0 }}
        >
          <DatabaseSearchContext.Provider value={{ search, loading: false }}>
            <ContentTypeRender path={model.path} items={model.item} />
          </DatabaseSearchContext.Provider>
        </div>
      )}
      {search?.error ? (
        <Button onClick={() => onRetry(slot)} type="warning">
          {app.t("Unable to load items. Retry")}
        </Button>
      ) : (
        !ready &&
        (model.loadingItem?.length ? (
          <div className="absolute inset-0 overflow-hidden">
            <LoadingItems model={model} />
          </div>
        ) : (
          <div className="loading absolute inset-x-0 top-0 m-4 h-4 max-w-xl rounded-lg p-4 shadow" />
        ))
      )}
    </div>
  );
};

const ViewListDynamicInfinite = ({ view, model }) => {
  const { search, loading } = useContext(DatabaseSearchContext);
  const location = useLocation();
  const ref = useRef();
  const runtime = useRef();
  const request = useRef(Promise.resolve());
  const current = useRef();
  const [layout, setLayout] = useState({ offsets: [0], estimate: model.slotMinHeight || 20, start: 0, end: 0 });
  const [pages, setPages] = useState(new Map());
  const slotsTotal = Math.ceil((search.searchTotal || 0) / (search.count || 1));
  // Results and cache metadata do not change the identity of the underlying query.
  const query = Object.fromEntries(
    Object.entries(search).filter(
      ([key]) =>
        !["data", "searchTotal", "total", "isDirty", "inCache", "offline", "error", "callbackId", "position"].includes(
          key,
        ),
    ),
  );
  const queryKey = JSON.stringify(query);
  current.current = { search, query, loading, model, slotsTotal };

  const onMeasure = useCallback((slot, height) => runtime.current?.measure(slot, height), []);
  const onRetry = useCallback((slot) => {
    runtime.current.pages.delete(slot);
    runtime.current.load();
  }, []);

  useEffect(() => {
    const element = ref.current;
    const parent = getScrollParent(element);
    const rt = {
      active: true,
      pages: new Map(),
      stale: new Set(),
      heights: new Map(),
      pending: new Map(),
      offsets: [0],
      estimate: current.current.model.slotMinHeight || 20,
      start: 0,
      end: 0,
      anchor: { slot: 0 },
      revision: 0,
      scrolling: false,
      dragging: false,
      fetching: false,
    };
    runtime.current = rt;
    let frame;
    let measureFrame;
    let settleTimer;
    let loadTimer;
    let correcting = false;
    let width = element.clientWidth;

    const geometry = () => {
      const scroll = parent === window ? window.scrollY : parent.scrollTop;
      const viewport = parent === window ? window.innerHeight : parent.clientHeight;
      const viewportTop = parent === window ? 0 : parent.getBoundingClientRect().top + parent.clientTop;
      const origin = element.getBoundingClientRect().top - viewportTop + scroll;
      return { scroll, viewport, viewportTop, origin, top: Math.max(0, scroll - origin) };
    };
    const slotAt = (top) => {
      let low = 0;
      let high = Math.min(current.current.slotsTotal, rt.offsets.length - 1);
      while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (rt.offsets[middle + 1] <= top) low = middle + 1;
        else high = middle;
      }
      return Math.max(0, Math.min(low, current.current.slotsTotal - 1));
    };
    const slotElements = () => element.querySelectorAll(":scope > [data-infinite-slot]");
    const setScroll = (scroll) => {
      // Any scroll write cancels a running smooth wheel scroll, so an unchanged position is not written.
      if (Math.abs(scroll - (parent === window ? window.scrollY : parent.scrollTop)) < 1) return;
      correcting = true;
      if (parent === window) window.scrollTo({ top: scroll, behavior: "instant" });
      else parent.scrollTop = scroll;
      requestAnimationFrame(() => {
        correcting = false;
      });
    };

    rt.load = async () => {
      if (!rt.active || rt.fetching || current.current.loading) return;
      // Missing batches load before stale ones, nearest to the visible batch first.
      const priority = (slot) =>
        (rt.pages.has(slot) ? current.current.slotsTotal : 0) + Math.abs(slot - rt.anchor.slot);
      let slot;
      for (let pos = rt.start; pos < rt.end; pos++)
        if (
          pos !== 0 &&
          (!rt.pages.has(pos) || rt.stale.has(pos)) &&
          (slot === undefined || priority(pos) < priority(slot))
        )
          slot = pos;
      if (slot === undefined) return;
      const { revision } = rt;
      rt.fetching = true;
      // This chain also serializes requests across query changes, while an old request finishes.
      request.current = request.current
        .catch(() => {})
        .then(async () => {
          if (!rt.active || revision !== rt.revision || slot < rt.start || slot >= rt.end) return;
          let result;
          try {
            result = await app.database[view.contentType].search({
              ...current.current.query,
              position: slot * current.current.search.count,
              data: undefined,
            });
          } catch (error) {
            result = { error: error.message };
          }
          if (!rt.active || revision !== rt.revision) return;
          rt.stale.delete(slot);
          // A failed refresh keeps the batch that is already shown.
          if (result.data || !rt.pages.get(slot)?.data)
            rt.pages.set(slot, result.data ? result : { error: result.error || "Search failed" });
          const cacheLimit = Math.max(12, (rt.end - rt.start) * 3);
          for (const slot of rt.pages.keys()) {
            if (rt.pages.size <= cacheLimit) break;
            if (slot !== 0 && (slot < rt.start || slot >= rt.end)) {
              rt.pages.delete(slot);
              rt.stale.delete(slot);
            }
          }
          setPages(new Map(rt.pages));
        });
      await request.current;
      rt.fetching = false;
      if (rt.active) rt.load();
    };

    const update = () => {
      frame = undefined;
      if (!rt.active) return;
      const { top, viewport, viewportTop } = geometry();
      const slot = slotAt(top);
      // A sticky header can cover the viewport top, so the list is anchored where it is first visible.
      const { left, width, top: listTop } = element.getBoundingClientRect();
      const visible = (y) => element.contains(document.elementFromPoint(left + width / 2, y));
      let visibleTop = Math.max(viewportTop, listTop);
      let hidden = viewportTop + viewport - 1;
      if (!visible(visibleTop) && visible(hidden)) {
        [visibleTop, hidden] = [hidden, visibleTop];
        while (visibleTop - hidden > 2) {
          const middle = Math.floor((visibleTop + hidden) / 2);
          if (visible(middle)) visibleTop = middle;
          else hidden = middle;
        }
      }
      // The first rendered element crossing that line keeps its screen position when heights above it change.
      // It is looked up in every batch, since a batch whose height is not yet measured can extend past its offsets.
      // Where batches overlap, the later one is painted on top and is the one in view.
      const contents = [...element.querySelectorAll(":scope > [data-infinite-slot] > [data-infinite-content]")];
      let item =
        contents.findLast(
          (content) =>
            content.getBoundingClientRect().top <= visibleTop && content.getBoundingClientRect().bottom > visibleTop,
        ) || contents.find((content) => content.getBoundingClientRect().bottom > visibleTop);
      // The path down to the element is kept, since rows can replace their inner elements, like a loaded image.
      const items = [];
      while (item) {
        items.push({ item, itemTop: item.getBoundingClientRect().top });
        if (item.getBoundingClientRect().top >= visibleTop) break;
        item = [...item.children].find((row) => row.getBoundingClientRect().bottom > visibleTop);
      }
      rt.anchor = { slot, items };
      const total = current.current.slotsTotal;
      rt.start = Math.max(0, slot - 2);
      rt.end = Math.min(total, Math.max(slotAt(top + viewport) + 3, rt.start + (current.current.model.slotLimit || 3)));
      rt.start = Math.max(0, Math.min(rt.start, rt.end - (current.current.model.slotLimit || 3)));
      setLayout({ offsets: rt.offsets, estimate: rt.estimate, start: rt.start, end: rt.end });
      view.event("infiniteScrollPos", { pos: rt.start, total: Math.max(0, total - (rt.end - rt.start) + 1) });
      clearTimeout(loadTimer);
      loadTimer = setTimeout(rt.load, current.current.model.searchDelay ?? 100);
    };
    rt.schedule = () => {
      if (frame === undefined) frame = requestAnimationFrame(update);
    };

    // A fixed estimate keeps unmeasured batches above the viewport in place while the user scrolls.
    const rebuild = (keepEstimate) => {
      const { slotsTotal: total, search } = current.current;
      const measured = [...rt.heights].filter(
        ([slot]) => slot < total && (slot < total - 1 || search.searchTotal % search.count === 0),
      );
      if (!keepEstimate && measured.length)
        rt.estimate = measured.reduce((sum, [, height]) => sum + height, 0) / measured.length;
      rt.offsets = [0];
      for (let slot = 0; slot < total; slot++)
        rt.offsets.push(rt.offsets[slot] + (rt.heights.get(slot) ?? rt.estimate));
    };
    // Commit the same offsets to the wrapper and all slots before correcting scroll position.
    const commit = () => {
      element.style.height = `${rt.offsets.at(-1)}px`;
      for (const child of slotElements()) child.style.top = `${rt.offsets[Number(child.dataset.infiniteSlot)] || 0}px`;
    };
    // Moves to the batch holding a search position; the batch then loads and its rows can be aligned by the caller.
    // Offsets only match the batches once a batch height is measured, so the move waits for that.
    const scrollToPosition = ({ position = rt.position } = {}) => {
      rt.position = position;
      if (position === undefined || !rt.heights.size) return;
      rt.position = undefined;
      setScroll(geometry().origin + rt.offsets[Math.floor(position / current.current.search.count)]);
      rt.schedule();
    };
    const apply = () => {
      if (!rt.active || rt.scrolling || rt.dragging) return;
      const { origin, scroll, viewport } = geometry();
      const anchorSlot = slotAt(Math.max(0, scroll - origin));
      const anchor = { slot: anchorSlot, offset: scroll - origin - (rt.offsets[anchorSlot] || 0) };
      const atEnd =
        scroll + viewport >= (parent === window ? document.documentElement.scrollHeight : parent.scrollHeight) - 2;
      for (const [slot, height] of rt.pending) rt.heights.set(slot, height);
      rt.pending.clear();
      rebuild();
      commit();
      if (scroll > origin && current.current.slotsTotal) {
        const top = Math.max(
          0,
          atEnd
            ? (parent === window ? document.documentElement.scrollHeight : parent.scrollHeight) - origin - viewport
            : Math.min((rt.offsets[anchor.slot] || 0) + anchor.offset, rt.offsets.at(-1) - viewport),
        );
        // The anchor also holds at the end, where content growing below it, like an opened panel, must not scroll it away.
        const anchorItem = rt.anchor.items?.findLast(({ item }) => item.isConnected);
        setScroll(
          anchorItem
            ? (parent === window ? window.scrollY : parent.scrollTop) +
                anchorItem.item.getBoundingClientRect().top -
                anchorItem.itemTop
            : origin + top,
        );
      }
      scrollToPosition();
      rt.schedule();
    };
    const flush = () => {
      measureFrame = undefined;
      if (!rt.active) return;
      if (!rt.scrolling && !rt.dragging) {
        apply();
        return;
      }
      // While the user scrolls, batches from the visible one down are committed at once since they never move the viewport.
      const visible = slotAt(geometry().top);
      let changed = false;
      for (const [slot, height] of rt.pending) {
        if (slot < visible) continue;
        rt.heights.set(slot, height);
        rt.pending.delete(slot);
        changed = true;
      }
      if (!changed) return;
      rebuild(true);
      commit();
      rt.schedule();
    };
    rt.measure = (slot, height) => {
      if (height < 1 || Math.abs((rt.pending.get(slot) ?? rt.heights.get(slot) ?? 0) - height) < 1) return;
      rt.pending.set(slot, height);
      if (measureFrame === undefined) measureFrame = requestAnimationFrame(flush);
    };
    const settle = () => {
      rt.scrolling = false;
      apply();
    };
    const onScroll = () => {
      if (correcting) {
        rt.schedule();
        return;
      }
      rt.scrolling = true;
      rt.position = undefined;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 150);
      rt.schedule();
    };
    const onPointerDown = (event) => {
      // Native scrollbar drags target the scrolling element rather than a content control.
      if (event.target === document.documentElement || event.target === document.body || event.target === parent)
        rt.dragging = true;
    };
    const onPointerUp = () => {
      rt.dragging = false;
      if (!rt.scrolling) apply();
    };
    // Browsers do not always deliver pointerup after a scrollbar drag; the next move without buttons ends it.
    const onPointerMove = (event) => {
      if (rt.dragging && !event.buttons) onPointerUp();
    };
    rt.invalidateHeights = () => {
      rt.heights.clear();
      rt.pending.clear();
      for (const child of slotElements()) {
        const content = child.querySelector("[data-infinite-content]");
        if (content?.offsetHeight && !content.querySelector(".loading, .render-suspense-loading"))
          rt.pending.set(Number(child.dataset.infiniteSlot), content.offsetHeight);
      }
      apply();
    };
    const onResize = () => {
      if (element.clientWidth !== width) {
        width = element.clientWidth;
        rt.invalidateHeights();
      }
      rt.schedule();
    };
    const observer = new ResizeObserver(onResize);
    observer.observe(element);
    if (parent !== window) observer.observe(parent);
    parent.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    rt.refresh = () => {
      if (rt.search === current.current.search) return;
      rt.search = current.current.search;
      rt.revision++;
      // Visible batches stay on screen until their refreshed data arrives; others are fetched again when needed.
      for (const slot of rt.pages.keys()) {
        if (slot >= rt.start && slot < rt.end) rt.stale.add(slot);
        else rt.pages.delete(slot);
      }
      rt.pages.set(0, rt.search);
      rt.stale.delete(0);
      setPages(new Map(rt.pages));
      if (current.current.model.clearHeightOnUpdate) rt.invalidateHeights();
      rebuild();
      rt.schedule();
    };
    view.on("infiniteScrollTo", scrollToPosition);
    rt.refresh();
    return () => {
      rt.active = false;
      view.off("infiniteScrollTo", scrollToPosition);
      cancelAnimationFrame(frame);
      cancelAnimationFrame(measureFrame);
      clearTimeout(settleTimer);
      clearTimeout(loadTimer);
      observer.disconnect();
      parent.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, [queryKey, view]);

  useEffect(() => {
    runtime.current?.refresh();
  }, [search]);
  useEffect(() => {
    runtime.current?.invalidateHeights();
  }, [location.search]);

  const slots = [];
  for (let slot = layout.start; slot < Math.min(layout.end, slotsTotal); slot++) {
    slots.push(
      <InfiniteSlot
        key={`${queryKey}:${slot}`}
        slot={slot}
        top={layout.offsets[slot] || 0}
        height={layout.offsets[slot + 1] - layout.offsets[slot] || layout.estimate}
        search={slot === 0 ? search : pages.get(slot)}
        model={model}
        onMeasure={onMeasure}
        onRetry={onRetry}
      />,
    );
  }
  return (
    <Wrapper
      ref={ref}
      className={model.className}
      style={{ position: "relative", height: slotsTotal ? layout.offsets.at(-1) : undefined, overflowAnchor: "none" }}
    >
      {slotsTotal ? (
        slots
      ) : loading ? (
        model.loadingItem?.length ? (
          <LoadingItems model={model} />
        ) : (
          <Loading />
        )
      ) : (
        <ContentTypeRender path={model.path} items={model.item} />
      )}
    </Wrapper>
  );
};

export default ViewListDynamicInfinite;
